import { isVisible, resolveAnchor, openAccordion, submenuAnchor, type AnchorSpec } from '../engine/anchors';
import {
  clickWhenReady,
  fillElement,
  hoverElement,
  findVisible,
  isFilled,
  pressElement,
  pressWhenReady,
  waitForAnchor,
  waitUntil,
} from './formFill';
import { bringIntoView, flashAnchor, flashElement } from './visibleAction';
import { closeFlowChrome } from './closeChrome';
import type { TaskFlow } from './types';

const FORMS_PATH = '/home/flows';

const DIALOG = '[role="dialog"], [role="alertdialog"]';

const buttonLabelled = (label: string, within: string): AnchorSpec => ({
  structural: { within, match: 'button', labels: [label] },
});

const menuItemLabelled = (label: string): AnchorSpec => ({
  structural: { within: '[role="menu"]', match: '[role="menuitem"]', labels: [label] },
});

const NEW_FORM_BUTTON = buttonLabelled('New form', '[data-testid="automations-filter-bar-actions"]');

const ADD_BLOCK_BUTTON = buttonLabelled('Add block', DIALOG);

const FOCUS_ATTR = 'data-tour-form-focus';

function focusOn(el: Element | null | undefined): void {
  clearFormFocus();
  el?.setAttribute(FOCUS_ATTR, '');
}

function clearFormFocus(): void {
  document.querySelectorAll(`[${FOCUS_ATTR}]`).forEach((el) => el.removeAttribute(FOCUS_ATTR));
}

const ADD_FIELD_TARGET: AnchorSpec = {
  selector: `[${FOCUS_ATTR}]`,
  structural: ADD_BLOCK_BUTTON.structural,
};

const TEXT_ANSWER_ITEM = menuItemLabelled('Text answer');
const SHORT_ANSWER_ITEM = menuItemLabelled('Short answer');

const SIDEBAR_ROW = submenuAnchor(['Automations'], 'Forms');

const onFormsPage = () =>
  window.location.pathname.startsWith(FORMS_PATH) && Boolean(resolveAnchor(NEW_FORM_BUTTON));

const composerOpen = () => Boolean(findVisible('#flow-name-visual'));

const isSaved = () => Boolean(resolveAnchor(buttonLabelled('Delete', DIALOG)));

function fieldRowTriggers(): HTMLElement[] {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>(DIALOG));
  return dialogs
    .flatMap((dialog) =>
      Array.from(dialog.querySelectorAll<HTMLElement>('button[data-orientation][aria-controls]')),
    )
    .filter(isVisible);
}

function rowOf(trigger: HTMLElement): HTMLElement {
  return trigger.closest<HTMLElement>('div[data-state][data-orientation]') ?? trigger;
}

function contentOf(trigger: HTMLElement): HTMLElement | null {
  const id = trigger.getAttribute('aria-controls');
  const content = id ? document.getElementById(id) : null;
  return content ?? null;
}

async function expandRow(trigger: HTMLElement): Promise<HTMLElement | null> {
  if (trigger.getAttribute('data-state') !== 'open') trigger.click();
  const opened = await waitUntil(() => Boolean(contentOf(trigger)?.querySelector('input')), 2500);
  return opened ? contentOf(trigger) : null;
}

function labelInputOf(content: HTMLElement | null): HTMLInputElement | null {
  return content?.querySelector('input') ?? null;
}

function openFieldLabels(): string[] {
  return fieldRowTriggers()
    .map((trigger) => labelInputOf(contentOf(trigger))?.value.trim().toLowerCase() ?? '')
    .filter(Boolean);
}

function hasFieldLabelled(label: string): boolean {
  return openFieldLabels().includes(label.trim().toLowerCase());
}

async function setInputType(content: HTMLElement, value: string): Promise<boolean> {
  const trigger = content.querySelector<HTMLElement>('button[role="combobox"]');
  if (!trigger) return false;
  if (trigger.getAttribute('data-state') !== 'open') pressElement(trigger);

  const wanted = value.toLowerCase();
  const findOption = () =>
    Array.from(document.querySelectorAll<HTMLElement>('[role="option"]')).find(
      (option) => (option.textContent ?? '').trim().toLowerCase() === wanted,
    ) ?? null;

  const appeared = await waitUntil(() => Boolean(findOption()), 2000);
  const option = appeared ? findOption() : null;
  if (!option) return false;

  await flashElement(option, { holdMs: 320 });
  hoverElement(option);
  pressElement(option);
  return waitUntil(() => (trigger.textContent ?? '').trim().toLowerCase() === wanted, 1500);
}

interface FieldSpec {
  id: string;
  label: string;
  inputType?: string;
}

const TEMPLATE_LABEL = 'text input';

const addedThisRun = new Set<string>();

async function addShortAnswerField(field: FieldSpec): Promise<void> {
  if (addedThisRun.has(field.id) || hasFieldLabelled(field.label)) {
    addedThisRun.add(field.id);
    return;
  }

  const before = fieldRowTriggers();

  focusOn(resolveAnchor(ADD_BLOCK_BUTTON)?.element);

  if (!(await pressWhenReady(ADD_BLOCK_BUTTON))) {
    console.warn('[tour-guide] "create-form": could not find/press the Add block button.');
    return;
  }
  if (!(await waitForAnchor(TEXT_ANSWER_ITEM, 2500))) {
    console.warn('[tour-guide] "create-form": the Add block menu never opened.');
    return;
  }

  focusOn(resolveAnchor(TEXT_ANSWER_ITEM)?.element);
  await flashAnchor(TEXT_ANSWER_ITEM);
  if (!(await pressWhenReady(TEXT_ANSWER_ITEM))) return;
  if (!(await waitForAnchor(SHORT_ANSWER_ITEM, 2500))) {
    console.warn('[tour-guide] "create-form": the Text answer submenu never opened.');
    return;
  }
  focusOn(resolveAnchor(SHORT_ANSWER_ITEM)?.element);
  await flashAnchor(SHORT_ANSWER_ITEM);
  if (!(await pressWhenReady(SHORT_ANSWER_ITEM))) return;

  const grew = await waitUntil(() => fieldRowTriggers().length > before.length, 4000);
  if (!grew) {
    console.warn('[tour-guide] "create-form": the new field row never appeared.');
    return;
  }
  const after = fieldRowTriggers();
  const fresh = after.filter((trigger) => !before.includes(trigger));
  const index = fresh.length === 1 ? after.indexOf(fresh[0]) : Math.max(before.length - 1, 0);
  const trigger = after[index] ?? after[after.length - 1];
  if (!trigger) return;

  const row = rowOf(trigger);
  focusOn(row);
  bringIntoView(row);
  await flashElement(row, { holdMs: 900, row: true });

  const live = trigger.isConnected ? trigger : fieldRowTriggers()[index];
  if (!live) {
    console.warn('[tour-guide] "create-form": lost the new field row before it could be labelled.');
    return;
  }

  const content = await expandRow(live);
  if (!content) {
    console.warn('[tour-guide] "create-form": the new field row would not open.');
    return;
  }
  bringIntoView(content);

  if (field.inputType) await setInputType(content, field.inputType);

  const input = labelInputOf(contentOf(live) ?? content);
  if (!input) {
    console.warn("[tour-guide] \"create-form\": could not find the new field's Label input.");
    return;
  }

  focusOn(input);
  await flashElement(input, { holdMs: 380 });
  const current = input.value.trim().toLowerCase();
  const writable = current === '' || current === TEMPLATE_LABEL || current === field.label.toLowerCase();
  if (writable) fillElement(input, field.label, { onlyIfEmpty: false });

  addedThisRun.add(field.id);
}

const two = (n: number) => String(n).padStart(2, '0');

function suggestedName(): string {
  const now = new Date();
  return `Product inquiry ${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
}

const SUGGESTED_NAME = suggestedName();

const NAME_FIELD: FieldSpec = { id: 'add-name-field', label: 'Name' };
const EMAIL_FIELD: FieldSpec = { id: 'add-email-field', label: 'Email', inputType: 'email' };
const MOBILE_FIELD: FieldSpec = { id: 'add-mobile-field', label: 'Mobile number', inputType: 'phone' };

const fieldPresent = (field: FieldSpec) => () =>
  addedThisRun.has(field.id) || hasFieldLabelled(field.label);

export const CREATE_FORM_TASK: TaskFlow = {
  id: 'create-form',
  stepId: 'automations--forms',
  cta: 'Try it: build a form',
  preparingLabel: 'Opening Forms…',
  title: 'Create a form',
  summary: 'Name, Email and Mobile number fields added for you — you press the buttons.',
  doneTitle: 'Form saved',
  doneDescription:
    'It is in your Forms list now, marked Draft, with Name, Email and Mobile number fields on it. Share it as a link or drop it into an automation once you are happy with the fields — submissions land in the CRM as leads. Publish to WhatsApp whenever you are ready to put it in front of customers.',

  beforeStart: async () => {
    addedThisRun.clear();
    clearFormFocus();
    if (onFormsPage()) return;
    openAccordion(['Automations']);
    await clickWhenReady(SIDEBAR_ROW);
    const arrived = await waitUntil(onFormsPage, 5000);
    if (!arrived) {
      console.warn(
        '[tour-guide] "create-form": never confirmed arrival on /home/flows ' +
          '(route and/or New form button). The next step may resolve the wrong button.',
      );
    }
  },

  afterEnd: async () => {
    await closeFlowChrome(composerOpen);
  },

  steps: [
    {
      id: 'open-composer',
      label: 'New form',
      title: 'Start a new form',
      description:
        'This is your Forms workspace — every form you have, shared as a link or dropped into an automation. New form opens the builder. Press Next to open it.',
      anchor: NEW_FORM_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFormsPage,
      prepare: async () => {},
      onNext: async () => {
        if (composerOpen()) return;
        await clickWhenReady(NEW_FORM_BUTTON);
        await waitUntil(composerOpen, 5000);
      },
      satisfied: composerOpen,
      rewind: async () => {
        await closeFlowChrome(composerOpen, 'rewind');
      },
      waitingHint: 'Press Next to open the builder.',
    },

    {
      id: 'name',
      label: 'Name',
      title: 'Name it',
      description:
        'Give the form a name — this is what you and your team see in the Forms list, not something a customer reads. Pick something that says what it collects.',
      anchor: { selector: '#flow-name-visual' },
      side: 'right',
      spotlight: true,
      fill: [['#flow-name-visual', SUGGESTED_NAME]],
      satisfied: () => isFilled('#flow-name-visual'),
      waitingHint: 'Type a name to carry on.',
    },

    {
      id: 'add-name-field',
      label: 'Name field',
      title: 'Add a Name field',
      description:
        'Add block is how every field gets onto the screen. Press Next and watch — the guide opens the menu, picks Text answer › Short answer, and the new field appears on the screen below, labelled Name for you.',
      anchor: ADD_FIELD_TARGET,
      side: 'right',
      prepare: async () => { clearFormFocus(); },
      onNext: () => addShortAnswerField(NAME_FIELD),
      satisfied: fieldPresent(NAME_FIELD),
      waitingHint: 'Press Next to add the field.',
    },

    {
      id: 'add-email-field',
      label: 'Email field',
      title: 'Add an Email field',
      description:
        'Same again for an email address. Input type is what tells WhatsApp how to check the answer — the guide sets this one to email, so a reply without an @ is rejected before it reaches you.',
      anchor: ADD_FIELD_TARGET,
      side: 'right',
      prepare: async () => { clearFormFocus(); },
      onNext: () => addShortAnswerField(EMAIL_FIELD),
      satisfied: fieldPresent(EMAIL_FIELD),
      waitingHint: 'Press Next to add the field.',
    },

    {
      id: 'add-mobile-field',
      label: 'Mobile field',
      title: 'Add a Mobile number field',
      description:
        'One more — a short answer for their mobile number, with Input type set to phone so WhatsApp shows a number pad and validates what they type. Three fields is plenty for a first form.',
      anchor: ADD_FIELD_TARGET,
      side: 'right',
      prepare: async () => { clearFormFocus(); },
      onNext: () => addShortAnswerField(MOBILE_FIELD),
      satisfied: fieldPresent(MOBILE_FIELD),
      waitingHint: 'Press Next to add the field.',
    },

    {
      id: 'save',
      label: 'Save',
      title: 'Save the draft',
      description:
        'Click Save draft. It lands in your Forms list marked Draft — nothing is shared or published until you choose to, so this is always safe to press.',
      anchor: buttonLabelled('Save draft', DIALOG),
      side: 'top',
      spotlight: true,
      advance: 'auto',
      satisfied: isSaved,
      waitingHint: 'Waiting for you to press Save draft…',
      optional: false,
    },

    {
      id: 'publish',
      label: 'Publish',
      title: 'Publish to WhatsApp',
      description:
        'This is the button that sends the form to Meta and makes it usable in a WhatsApp conversation. It needs a saved draft, a connected WhatsApp Business account and a form with no errors — until then it stays greyed out. Press it yourself when you are ready; a published form cannot be edited freely afterwards.',
      anchor: buttonLabelled('Publish to WhatsApp', DIALOG),
      side: 'top',
      spotlight: true,
      nextLabel: 'Finish',
    },
  ],
};
